import type { WorkCalendar } from '@hana/work-calendar';
import { dayRecord, type DayRecord } from '../data';
import type { MeeAttendance } from '../data';
import { dayNo, hm } from '../home/view';

const dayKey = (n: number): string => new Date(n * 86_400_000).toISOString().slice(0, 10);

export interface MonthCell {
  day: string;
  /** thuộc tháng đang xem (ô đệm đầu / cuối tuần thì false) */
  inMonth: boolean;
  rec: DayRecord;
}

/** Tháng kế (delta = ±1) của "YYYY-MM". */
export function shiftMonth(ym: string, delta: number): string {
  const d = new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7) - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

/** Lưới tháng, tuần bắt đầu thứ Hai (T2 … CN), đệm đủ tuần. */
export function monthGrid(cal: WorkCalendar, ym: string, today: string, recs: readonly MeeAttendance[]): MonthCell[] {
  const first = dayNo(`${ym}-01`);
  const next = dayNo(`${shiftMonth(ym, 1)}-01`);
  const lead = (((first + 3) % 7) + 7) % 7; // 1970-01-01 là thứ Năm → 0 = T2
  const total = Math.ceil((lead + (next - first)) / 7) * 7;
  const byDay = new Map(recs.map((r) => [r.day, r]));
  return Array.from({ length: total }, (_, i) => {
    const day = dayKey(first - lead + i);
    return { day, inMonth: day >= `${ym}-01` && day < `${shiftMonth(ym, 1)}-01`, rec: dayRecord(cal, day, today, byDay.get(day)) };
  });
}

export interface MonthStats {
  /** ngày công đủ giờ (kể cả nửa ngày nghỉ phép đủ ca còn lại) */
  full: number;
  short: number;
  leave: number;
  /** tổng phút đi muộn */
  lateMinutes: number;
  presence: number;
  /** giờ chuẩn của các ngày đã qua / hôm nay */
  standard: number;
}

export function monthStats(cells: readonly MonthCell[]): MonthStats {
  const r: MonthStats = { full: 0, short: 0, leave: 0, lateMinutes: 0, presence: 0, standard: 0 };
  for (const c of cells) {
    if (!c.inMonth) continue;
    const { status, presence, lateMinutes, standardHours } = c.rec;
    if (status === 'ok' || status === 'halfLeave') r.full++;
    if (status === 'short') r.short++;
    if (status === 'leave') r.leave++;
    if (status === 'halfLeave') r.leave += 0.5;
    if (status === 'ok' || status === 'short' || status === 'halfLeave') {
      r.presence += presence;
      r.lateMinutes += lateMinutes;
      r.standard += standardHours;
    }
  }
  return r;
}

export interface MonthKpi {
  /** công chuẩn của tháng theo lịch công ty (ngày lễ vẫn tính) */
  stdDays: number;
  /** Σ giờ có mặt (tối đa giờ chuẩn của ngày) ÷ giờ ngày đủ; phép tính riêng */
  actualDays: number;
  lateDays: number;
  lateMinutes: number;
  /** giờ có mặt vượt giờ chuẩn ngày */
  otHours: number;
  mix: { full: number; short: number; leave: number; holiday: number };
}

/** Số liệu đầu trang Chấm công theo board `Mint-Mee` (tm.kpi / tm.mix). */
export function monthKpi(cal: WorkCalendar, ym: string, cells: readonly MonthCell[]): MonthKpi {
  const inM = cells.filter((c) => c.inMonth);
  const fullDay = Math.max(1, ...inM.map((c) => c.rec.standardHours));
  const past = inM.filter((c) => ['ok', 'short', 'leave', 'halfLeave', 'today'].includes(c.rec.status));
  const k: MonthKpi = { stdDays: cal.standardDaysInMonth(ym), actualDays: 0, lateDays: 0, lateMinutes: 0, otHours: 0, mix: { full: 0, short: 0, leave: 0, holiday: 0 } };
  for (const { rec } of past) {
    const half = rec.status === 'halfLeave';
    if (rec.status !== 'leave') k.actualDays += Math.min(rec.presence, half ? rec.standardHours / 2 : rec.standardHours) / fullDay;
    if (rec.lateMinutes > 0) { k.lateDays++; k.lateMinutes += rec.lateMinutes; }
    k.otHours += Math.max(0, rec.presence - rec.standardHours);
  }
  for (const { rec } of inM) {
    if (rec.status === 'ok') k.mix.full++;
    else if (rec.status === 'short') k.mix.short++;
    else if (rec.status === 'leave' || rec.status === 'halfLeave') k.mix.leave++;
    else if (rec.status === 'holiday') k.mix.holiday++;
  }
  return k;
}

/** Ghi giờ ra cho một ngày (giả lập chấm công): ngày chưa có dòng riêng lấy giờ vào mặc định theo ca (5 phút trước đầu ca). */
export function withClockOut(cal: WorkCalendar, owner: string, day: string, out: string, rec?: MeeAttendance): MeeAttendance {
  const start = cal.shiftsOf(day)[0]?.[0] ?? 0;
  return { id: `${owner}:${day}`, owner, day, in: rec ? rec.in : hm(start - 5), ...(rec?.half ? { half: true } : {}), out };
}
